import { useState, useEffect, useCallback, useRef } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDropzone } from "react-dropzone";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { createMeeting } from "../../features/meetings/meetingsSlice";
import { getAgents } from "../../features/agent/agentSlice";
import { upload, deleteFile } from "../../features/uploads/uploadSlice";
import { uploadComplete } from "../../features/resource/resourceSlice";
import {
  DROPZONE_ACCEPT,
  detectFileType,
  slugify,
  uploadToS3,
} from "./Meetings.utils";

const useCreateMeeting = ({ isOnboarding = false } = {}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { loading } = useSelector((state) => state.meetings);
  const { agents } = useSelector((state) => state.agent);

  const [uploadedFiles, setUploadedFiles] = useState([]);

  const generateMeetingName = () => `Meeting_${Date.now()}`;

  useEffect(() => {
    dispatch(getAgents());
  }, [dispatch]);

  const processFile = useCallback(
    async (file, type) => {
      const id = crypto.randomUUID();

      const patch = (p) =>
        setUploadedFiles((prev) =>
          prev.map((f) => (f.id === id ? { ...f, ...p } : f)),
        );

      setUploadedFiles((prev) => [
        ...prev,
        {
          id,
          file,
          type,
          status: "pending",
          progress: 0,
          resourceId: null,
          entityId: null,
          error: null,
        },
      ]);

      try {
        patch({ status: "uploading" });

        const { payload: data } = await dispatch(
          upload({
            type,
            body: {
              name: file.name,
              seoName: slugify(file.name),
              mimeType: file.type,
              ...(type === "recordings" ? { duration: 0 } : {}),
            },
          }),
        );

        const uploadUrl =
          data?.uploadUrl || data?.url || data?.presignedUrl || data?.signedUrl;
        const resourceId = data?.resource?.uuid || null;
        const entityId =
          type === "recordings" ? data?.recording?.uuid : data?.document?.uuid;

        if (!uploadUrl || !resourceId || !entityId) {
          throw new Error(
            "Upload init missing uploadUrl / resourceId / entityId",
          );
        }

        await uploadToS3(uploadUrl, file, (progress) => patch({ progress }));
        patch({ status: "completing", progress: 100, resourceId, entityId });
        await dispatch(uploadComplete({ resourceId, type, size: file.size }));

        patch({ status: "done", progress: 100, resourceId, entityId });
      } catch (err) {
        patch({
          status: "error",
          error: err?.response?.data?.message || err.message || "Upload failed",
        });
      }
    },
    [dispatch],
  );

  const onDrop = useCallback(
    (acceptedFiles) => {
      for (const file of acceptedFiles) {
        const type = detectFileType(file);
        processFile(file, type);
      }
    },
    [processFile],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: DROPZONE_ACCEPT,
    multiple: true,
  });

  const handleRemove = async (item) => {
    if (item.status === "uploading" || item.status === "removing") return;

    if (item.entityId) {
      setUploadedFiles((prev) =>
        prev.map((f) =>
          f.id === item.id ? { ...f, status: "removing", error: null } : f,
        ),
      );
      try {
        dispatch(deleteFile({ fileId: item.entityId, type: item.type }));
      } catch {
        toast.error("Failed to remove file.");
        setUploadedFiles((prev) =>
          prev.map((f) =>
            f.id === item.id
              ? { ...f, status: "error", error: "Delete failed" }
              : f,
          ),
        );
        return;
      }
    }

    setUploadedFiles((prev) => prev.filter((f) => f.id !== item.id));
  };

  const doneFiles = uploadedFiles.filter((f) => f.status === "done");
  const anyUploading = uploadedFiles.some(
    (f) => f.status === "uploading" || f.status === "completing",
  );

  const doneCounts = {
    recordings: doneFiles.filter((f) => f.type === "recordings").length,
    documents: doneFiles.filter((f) => f.type === "documents").length,
  };

  const agentSet = useRef(false);

  const formik = useFormik({
    initialValues: {
      name: generateMeetingName(),
      agent: "",
      rawText: "",
      participants: [],
    },

    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      agent: Yup.string().required("Agent is required"),
      participants: Yup.array().of(Yup.string()),
      rawText: Yup.string(),
    }),

    validate: (values) => {
      const errors = {};
      if (doneFiles.length === 0 && !values.rawText.trim()) {
        errors._content =
          "Please upload at least one file or  transcription text.";
      }
      return errors;
    },

    onSubmit: async (values, { resetForm }) => {
      try {
        const payload = {
          name: values.name,
          agentId: values.agent || undefined,
          participants: values.participants || [],
          recording: doneFiles
            .filter((f) => f.type === "recordings")
            .map((f) => f.entityId),
          document: doneFiles
            .filter((f) => f.type === "documents")
            .map((f) => f.entityId),
          ...(values.rawText.trim() ? { content: values.rawText } : {}),
        };

        const response = await dispatch(createMeeting(payload)).unwrap();
        resetForm();
        setUploadedFiles([]);
        if (isOnboarding) {
          navigate("/onboarding/meetings");
          return;
        }

        navigate(`/meetings/${response?.uuid}`);
      } catch (err) {
        toast.error(err?.message || "Failed to create meeting.");
      }
    },
  });
  const { setFieldValue } = formik;
  useEffect(() => {
    if (agents.length > 0 && !agentSet.current) {
      agentSet.current = true;
      const def = agents.find((a) => a.isDefault);
      if (def) setFieldValue("agent", def.uuid);
    }
  }, [agents, setFieldValue]);
  return {
    formik,
    agents,
    uploadedFiles,
    doneFiles,
    doneCounts,
    anyUploading,
    loading,
    handleRemove,
    getRootProps,
    getInputProps,
    isDragActive,
    navigate,
  };
};

export default useCreateMeeting;
